import './styles.scss';

import { memo, useMemo, useState } from 'react';

import { ReportFilterMultiSelect } from '../../../../../components/report-filter-buttons/report-filter-multi-select';
import { BacktesterStrategyData } from '../../../../../providers/backtesting/api/backtester-config-api.types';
import { PortfolioPerformanceTransactionsTable } from '../../../../portfolio-performance/components/cards/transactions-card/transactions-table';
import { buildTransactionRows } from './build-transaction-rows';

// Known backtester action types, in display order. Any action outside this set
// still renders in the table; it just isn't offered as a filter chip.
const ACTION_LABELS: Record<string, string> = {
  option_execution: 'Option execution',
  option_settlement: 'Option settlement',
  delta_hedge: 'Delta hedge',
  funding_payment: 'Funding payment',
};

const ACTION_FILTER_OPTIONS = Object.entries(ACTION_LABELS).map(([value, label]) => ({
  value,
  label,
  isVisible: true,
}));

interface TransactionLogProps {
  strategyData: BacktesterStrategyData;
  selectedStrategy: string;
}

// Reuses the live Portfolio Performance transactions table
// (PortfolioPerformanceTransactionsTable) for the open stratlet's transactions,
// so backtest fills render identically to live-account fills. It reuses the
// TABLE, not the whole TransactionsCard: the card's "Live Data (rolling 24h)"
// header and its metrics strip (which only counts event_type === 'trade', a
// live-only value the backtester never emits) don't apply to a completed run,
// so they're omitted rather than shown empty/misleading — same call as the
// Market Risk panel reusing RiskPanelGraph without the live filter bar.
export const TransactionLog = memo(({ strategyData, selectedStrategy }: TransactionLogProps) => {
  const scenario = strategyData[selectedStrategy];
  const token = scenario?.stratlet.account.token?.trim() || '';
  const transactions = useMemo(() => scenario?.transactions ?? [], [scenario]);

  const [selectedActions, setSelectedActions] = useState<string[]>(Object.keys(ACTION_LABELS));

  const rows = useMemo(() => {
    const filtered =
      selectedActions.length > 0
        ? transactions.filter((t) => selectedActions.includes(t.action ?? ''))
        : transactions;
    return buildTransactionRows(filtered, token);
  }, [transactions, selectedActions, token]);

  return (
    <section className="backtest-table-view backtest-result-view backtest-result-transaction-log">
      <div className="backtest-table-view__header">
        <h3>Transaction Log</h3>
        <div className="backtest-table-view__filters">
          <ReportFilterMultiSelect
            titleFilter="Action"
            dataArr={ACTION_FILTER_OPTIONS}
            selected={selectedActions}
            setSelected={setSelectedActions}
          />
        </div>
      </div>

      {/* variant='backtest': the table reads the engine's Transaction struct
          (spot/future/volatility/premium/fee_rate) and uses the backtest column
          set. runningPosition: the backtester emits no position book, so the
          table derives a running signed position per instrument from the ordered
          fills. Live Portfolio Performance uses neither and is unaffected. */}
      <PortfolioPerformanceTransactionsTable
        data={rows}
        loading={false}
        variant="backtest"
        runningPosition
      />
    </section>
  );
});

TransactionLog.displayName = 'TransactionLog';
